<script setup lang="ts">
import {computed, reactive, ref, watch} from 'vue'
import {ElMessage} from 'element-plus'
import {useI18n} from 'vue-i18n'
import {createInquiry, getInquiryMatchCount} from '~/api/inquiry'
import {checkSpaceSlot} from '~/api/space'
import {firstAvailable, submitBlocked, toSlotVerdict, UNKNOWN_VERDICT, type SlotVerdict} from '~/utils/slotVerdict'
import TurnstileWidget from '~/components/Inquiry/TurnstileWidget.vue'
import ShareButton from '~/components/Share/ShareButton.vue'
import {useSearchQueryStore} from '~/stores/searchQuery.js'
import {useRemarketing} from '~/composables/useRemarketing'
import {useBehaviorFlow} from '~/composables/useBehaviorFlow'

const props = defineProps<{
  spaceId: number
  spaceName: string
  visible: boolean
  // AV P6:詳情頁日曆的「用這個時段詢問」預填。走 props 而不是寫進搜尋 store——
  // 那個 store 是搜尋列的狀態,借它傳一次性的預填值會讓兩邊互相覆寫。
  seedDate?: string
  seedTime?: string
  seedBroadcast?: boolean
}>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
}>()

const {t} = useI18n()
const route = useRoute()
const searchQueryStore = useSearchQueryStore()
const {trackBeginInquiry} = useRemarketing()
const behaviorFlow = useBehaviorFlow()

// BI-UI v2:portal_contact(docs/behavior-insights/portal-merchant-ui/implementation.md §5)。
const FEATURE_ID = 'portal_contact'

// 分享歸因:落地頁的 ?ref=（web_share/card_share/combo_share…）帶進詢問;空字串=organic/unknown。
function shareSource(): string {
  const ref = route.query.ref
  return typeof ref === 'string' ? ref : ''
}

interface InquiryFormState {
  userName: string
  userEmail: string
  userPhone: string
  eventDate: string
  eventTime: string
  guestCount: string
  budget: string
  message: string
}

const form = reactive<InquiryFormState>({
  userName: '',
  userEmail: '',
  userPhone: '',
  eventDate: '',
  eventTime: '',
  guestCount: '',
  budget: '',
  message: ''
})

const captchaToken = ref<string>('')
const submitting = ref<boolean>(false)
const submitted = ref<boolean>(false)
// 詢問範圍二選一（Phase 2）：false=只詢問這個場地；true=同時詢問類似場地（broadcast）。
const broadcast = ref<boolean>(false)
const matchCount = ref<number | null>(null)

// AV P6:後端 /api/space/slot-check 的答案。預設 unknown ＝ 不擋——見 utils/slotVerdict.ts
// 的 fail-open:前端漏擋只是多跑一趟(後端那道閘照樣零寫入),前端誤擋是全站送不出詢問。
const slotVerdict = ref<SlotVerdict>(UNKNOWN_VERDICT)

const slotBlocked = computed<boolean>(() => submitBlocked(slotVerdict.value, broadcast.value))
const slotAvailable = computed<string>(() => slotVerdict.value.available.join('、'))
const slotQuickFix = computed<string>(() => firstAvailable(slotVerdict.value))

function close(): void {
  emit('update:visible', false)
}

function reset(): void {
  form.userName = ''
  form.userEmail = ''
  form.userPhone = ''
  form.eventDate = ''
  form.eventTime = ''
  form.guestCount = ''
  form.budget = ''
  form.message = ''
  broadcast.value = false
  matchCount.value = null
  submitted.value = false
  slotVerdict.value = UNKNOWN_VERDICT
}

let slotTimer: ReturnType<typeof setTimeout> | undefined
let slotSeq = 0

// 只問後端,不在 JS 裡比對時段——畫面上顯示的與 CreateInquiry 擋的必須是同一份判斷,
// 各算一套遲早漂移。
async function runSlotCheck(): Promise<void> {
  const seq = ++slotSeq
  if (form.eventDate === '') {
    slotVerdict.value = UNKNOWN_VERDICT
    return
  }

  let verdict = UNKNOWN_VERDICT
  try {
    verdict = toSlotVerdict(await checkSpaceSlot(props.spaceId, form.eventDate, form.eventTime))
  } catch {
    // 請求掛掉 ⇒ 維持 unknown ⇒ 不擋。
    verdict = UNKNOWN_VERDICT
  }

  // 改日期改得快時回應會亂序;只採信最後一次發問的答案。
  if (seq === slotSeq) {
    slotVerdict.value = verdict
  }
}

function scheduleSlotCheck(): void {
  if (slotTimer !== undefined) {
    clearTimeout(slotTimer)
  }
  slotTimer = setTimeout(runSlotCheck, 400)
}

watch([() => form.eventDate, () => form.eventTime], scheduleSlotCheck)

// ⛔ 方框裡的快捷鍵:填進去之後 watch 會自己重問一次,方框跟著消失。
function applyQuickFix(): void {
  form.eventTime = slotQuickFix.value
}

// BI-UI v2:mode 是這個 feature 的第一個 step(單一場地 vs 類似場地廣播)。
function selectContactMode(isBroadcast: boolean): void {
  broadcast.value = isBroadcast
  behaviorFlow.trackStep(FEATURE_ID, 'mode', 'attempt')
}

// 選「詢問類似場地」時預覽會同時詢問幾個場地；人數變動時重新計算。
async function refreshMatchCount(): Promise<void> {
  if (!broadcast.value) {
    matchCount.value = null
    return
  }
  const res = await getInquiryMatchCount(props.spaceId, toOptionalInt(form.guestCount))
  matchCount.value = typeof res?.match_count === 'number' ? res.match_count : null
}

watch([broadcast, () => form.guestCount], refreshMatchCount)

interface SeedRange {
  min?: number
  max?: number
}

interface SeedRentTime {
  rent_type?: number
  begin_time?: string
  end_time?: string
}

// AV P1(捕捉導向):開啟詢問表單時,用搜尋列已選的日期/人數/預算預填,少讓使用者重打。
// 只在對應欄位仍為空時填入,不覆蓋使用者已手動輸入的值。
function seedFromSearch(): void {
  const rentTime: SeedRentTime = searchQueryStore.getRentTimeSelected ?? {}
  if (rentTime.begin_time && form.eventDate === '') {
    form.eventDate = rentTime.begin_time.split(' ')[0].split('/').join('-')
    if (rentTime.rent_type !== 1) {
      const start = rentTime.begin_time.split(' ')[1]?.slice(0, 5) ?? ''
      const end = rentTime.end_time?.split(' ')[1]?.slice(0, 5) ?? ''
      if (start !== '' && end !== '' && !(start === '00:00' && end === '23:59')) {
        form.eventTime = `${start}~${end}`
      }
    }
  }

  const people: SeedRange = searchQueryStore.getPeopleCapRange ?? {}
  if (typeof people.min === 'number' && form.guestCount === '') {
    form.guestCount = String(people.min)
  }

  const price: SeedRange = searchQueryStore.getPriceRange ?? {}
  if (form.budget === '') {
    const budget = typeof price.max === 'number' && price.max < 999999 ? price.max : price.min
    if (typeof budget === 'number') {
      form.budget = String(budget)
    }
  }
}

// 詳情頁日曆點某一天的「用這個時段詢問」。這是租客剛剛挑的,優先於搜尋列的舊值,
// 所以直接覆寫(seedFromSearch 只填空欄位,跑在後面不會蓋回去)。
function seedFromProps(): void {
  if (props.seedDate) {
    form.eventDate = props.seedDate
  }
  if (props.seedTime) {
    form.eventTime = props.seedTime
  }
  if (props.seedBroadcast) {
    broadcast.value = true
  }
}

watch(() => props.visible, (isVisible) => {
  if (isVisible) {
    seedFromProps()
    seedFromSearch()
    runSlotCheck()
    // DG P3 再行銷受眾:開了表單但沒送出的人 = begin_inquiry 與 submit_inquiry 的差集。
    // 這是意圖最高、卻最容易流失的一群。
    trackBeginInquiry({ spaceId: props.spaceId, broadcast: broadcast.value })
    // BI-UI v2:開啟表單＝這個任務的起點(第一個 step 是 mode)。
    behaviorFlow.startTask(FEATURE_ID, 'mode', 'create')
  }
})

function toOptionalInt(value: string): number | undefined {
  const trimmed = value.trim()
  if (trimmed === '') {
    return undefined
  }
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : undefined
}

async function handleSubmit(): Promise<void> {
  // 送出鈕的 pointer-events-none 擋不住鍵盤與程式呼叫,所以這裡再擋一次。
  if (slotBlocked.value) {
    ElMessage({message: t('inquiry.slot_unavailable_blocked_hint'), type: 'warning', duration: 3000})
    behaviorFlow.trackFailed(FEATURE_ID, 'submit', 'conflict')
    return
  }
  if (form.userName.trim() === '' || form.userEmail.trim() === '' || form.message.trim() === '') {
    ElMessage({message: t('inquiry.required_hint'), type: 'warning', duration: 3000})
    behaviorFlow.trackFailed(FEATURE_ID, 'submit', 'validation')
    return
  }
  if (captchaToken.value === '') {
    ElMessage({message: t('inquiry.captcha_required'), type: 'warning', duration: 3000})
    behaviorFlow.trackFailed(FEATURE_ID, 'submit', 'challenge')
    return
  }

  behaviorFlow.trackStep(FEATURE_ID, 'submit', 'attempt')
  submitting.value = true
  try {
    const analyticsContext = behaviorFlow.analyticsContext(FEATURE_ID)
    const result = await createInquiry({
      space_id: props.spaceId,
      user_name: form.userName,
      user_email: form.userEmail,
      user_phone: form.userPhone,
      event_date: form.eventDate,
      event_time: form.eventTime,
      guest_count: toOptionalInt(form.guestCount),
      budget: toOptionalInt(form.budget),
      message: form.message,
      captcha_token: captchaToken.value,
      source: shareSource(),
      broadcast: broadcast.value,
      // BI-UI v2:把這次建單跟同一次任務的 client 端事件串起來(implementation.md §4.4)。
      analytics_context: analyticsContext.flow_id
        ? {flow_id: analyticsContext.flow_id, session_id: analyticsContext.session_id}
        : undefined
    })
    if (result) {
      submitted.value = true
      behaviorFlow.trackStep(FEATURE_ID, 'submit', 'client_confirmed')
      behaviorFlow.trackContactIntent(
        FEATURE_ID,
        'web',
        broadcast.value ? 'similar' : 'single',
        broadcast.value ? matchCount.value ?? undefined : undefined
      )
      behaviorFlow.completeTask(FEATURE_ID)

      // GTM:漏斗底部「送出詢問」事件 — 補齊「進站→搜尋→詢問」轉換與來源歸因
      // (北極星量測,見 docs/demand-growth/proposal-demand-growth.md §4)。
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'submit_inquiry',
        target_type: broadcast.value ? 'broadcast' : 'space',
        space_id: props.spaceId,
        source: shareSource()
      })
    } else {
      // 送出失敗有可能是後端那道閘擋下的競態(問過 slot-check 之後才被別人訂走,IQ0014)。
      // 錯誤碼被 composables/requset.ts 吞掉拿不到,所以直接重問一次:真的撞到,⛔ 方框
      // 就會就地出現、按鈕跟著鎖上。畫面不跳走、欄位不清空(submitted 只在成功時翻)。
      behaviorFlow.trackFailed(FEATURE_ID, 'submit', 'conflict')
      await runSlotCheck()
    }
  } finally {
    submitting.value = false
  }
}

function handleClose(): void {
  close()
  reset()
}
</script>

<template>
  <el-dialog
    :model-value="props.visible"
    width="90%"
    center
    align-center
    class="inquiry-dialog max-w-[560px]"
    @update:model-value="handleClose"
  >
    <template #header>
      <h2 class="text-[20px] font-bold text-osn-dark-grey-00">
        {{ t('inquiry.title', {name: props.spaceName}) }}
      </h2>
    </template>

    <div v-if="!submitted" class="flex flex-col gap-3">
      <p class="text-[14px] text-osn-grey-01">{{ t('inquiry.subtitle') }}</p>

      <!-- 詢問範圍二選一（Phase 2 broadcast）：明確 opt-in，取代隱性 toggle -->
      <div class="flex gap-2">
        <div
          class="flex-1 text-center py-2 px-3 text-[14px] rounded-[10px] border cursor-pointer"
          :class="!broadcast ? 'border-osn-yellow-00 bg-osn-yellow-00/10 font-bold text-osn-dark-grey-00' : 'border-osn-grey-02 text-osn-grey-01'"
          @click="selectContactMode(false)"
        >
          {{ t('inquiry.scope_single') }}
        </div>
        <div
          class="flex-1 text-center py-2 px-3 text-[14px] rounded-[10px] border cursor-pointer"
          :class="broadcast ? 'border-osn-yellow-00 bg-osn-yellow-00/10 font-bold text-osn-dark-grey-00' : 'border-osn-grey-02 text-osn-grey-01'"
          @click="selectContactMode(true)"
        >
          {{ t('inquiry.scope_broadcast') }}
        </div>
      </div>
      <p v-if="broadcast" class="text-[13px] text-osn-grey-01">
        {{ matchCount && matchCount > 0 ? t('inquiry.scope_broadcast_count', {count: matchCount}) : t('inquiry.scope_broadcast_hint') }}
      </p>

      <el-input v-model="form.userName" :placeholder="t('inquiry.name')" size="large"/>
      <el-input v-model="form.userEmail" :placeholder="t('inquiry.email')" size="large"/>
      <el-input v-model="form.userPhone" :placeholder="t('inquiry.phone')" size="large"/>
      <div class="flex gap-3">
        <!-- el-date-picker(非原生 input type=date):原生 input 在 mobile 不顯 placeholder→看似空白;
             改用 el-date-picker 有 placeholder + 一致日曆。value-format 保持 YYYY-MM-DD 字串。 -->
        <el-date-picker v-model="form.eventDate" type="date" :placeholder="t('inquiry.event_date')"
                        size="large" format="YYYY-MM-DD" value-format="YYYY-MM-DD" class="!w-full"/>
        <el-input v-model="form.eventTime" :placeholder="t('inquiry.time')" size="large"/>
      </div>

      <!-- AV P6 ⛔ 撞檔期。只有後端回 partial / full 才出現;unknown / closed / none
           與請求失敗一律不出現、按鈕不鎖(fail-open,見 utils/slotVerdict.ts)。 -->
      <div v-if="slotBlocked"
           class="flex flex-col gap-2 p-3 rounded-[10px] border border-osn-red-00 bg-osn-red-00/5 text-[14px] text-osn-dark-grey-00">
        <p class="font-bold text-osn-red-00">
          ⛔
          {{
            slotVerdict.conflict === 'full'
                ? t('inquiry.slot_unavailable_full', {date: form.eventDate})
                : t('inquiry.slot_unavailable_partial')
          }}
        </p>
        <p v-if="slotAvailable !== ''">{{ t('inquiry.slot_unavailable_available', {slots: slotAvailable}) }}</p>
        <p v-else>{{ t('inquiry.slot_unavailable_full_hint') }}</p>
        <div class="flex flex-wrap gap-2">
          <div v-if="slotQuickFix !== ''"
               class="py-1 px-3 rounded-[50px] border border-osn-yellow-00 bg-osn-yellow-00/10 font-bold cursor-pointer"
               @click="applyQuickFix">
            {{ t('inquiry.slot_unavailable_apply', {slot: slotQuickFix}) }}
          </div>
          <!-- 廣播是真的出路:後端會把撞期的主場地剔掉,其餘照常寄出。 -->
          <div class="py-1 px-3 rounded-[50px] border border-osn-grey-02 cursor-pointer"
               @click="selectContactMode(true)">
            {{ t('inquiry.slot_unavailable_broadcast') }}
          </div>
        </div>
      </div>
      <div class="flex gap-3">
        <el-input v-model="form.guestCount" type="number" :placeholder="t('inquiry.guests')" size="large"/>
        <el-input v-model="form.budget" type="number" :placeholder="t('inquiry.budget')" size="large"/>
      </div>
      <el-input v-model="form.message" type="textarea" :rows="4" :placeholder="t('inquiry.message')"/>

      <TurnstileWidget @verified="(token) => (captchaToken = token)"/>

      <div
        class="mt-2 flex items-center justify-center w-full h-[50px] text-[18px] font-bold rounded-[50px] text-osn-dark-grey-00 bg-osn-yellow-00 cursor-pointer"
        :class="submitting || slotBlocked ? 'opacity-60 pointer-events-none' : ''"
        @click="handleSubmit"
      >
        {{ t('inquiry.submit') }}
      </div>
    </div>

    <div v-else class="flex flex-col items-center gap-4 py-4 text-center">
      <svg-icon name="check" size="48"/>
      <p class="text-[18px] font-bold text-osn-dark-grey-00">{{ t('inquiry.success_title') }}</p>
      <p class="text-[14px] text-osn-grey-01">{{ t('inquiry.success_desc') }}</p>

      <!-- 詢問送出=高意圖時機:邀使用者把場地分享給一起決定的夥伴(團體決策) -->
      <p class="text-[14px] font-bold text-osn-dark-grey-00 mt-2">{{ t('inquiry.share_prompt') }}</p>
      <ShareButton :target="`/space/${spaceId}`"
                   source="inquiry_share"
                   :share-text="spaceName"></ShareButton>

      <div
        class="flex items-center justify-center px-8 h-[44px] text-[16px] font-bold rounded-[50px] text-osn-dark-grey-00 bg-osn-yellow-00 cursor-pointer"
        @click="handleClose"
      >
        {{ t('inquiry.success_cta') }}
      </div>
    </div>
  </el-dialog>
</template>

<!-- not scoped: el-dialog teleports to <body>, so the style must be global;
     namespaced by the unique .inquiry-dialog class to avoid leaking. -->
<style>
.inquiry-dialog.el-dialog {
  border-radius: 16px;
  overflow: hidden;
}
</style>
